/* eslint-disable @typescript-eslint/no-namespace */
import { BaseStateInterface } from './basestate.state';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { QuestionnaireRowDto } from 'app/@types/dto/questionnaire-row-dto.model';
import { AdvancedMeasureHttpService } from '@services/datasources/advanced-measure-http.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { BatchUpdateRequestDto } from 'app/@types/dto/batch-update-request-dto.model';
import { QuestionnaireAction } from './questionnaire.state';
import { FileInfoDto } from 'app/@types/dto/file-info-dto.model';
import { CopyToMultipleDestinationsResponseDto } from 'app/@types/dto/copy-to-multiple-destinations-response-dto.model';

// ------Manages global state, coordinates operations------ //

export interface AdvancedMeasureModel extends BaseStateInterface {
    measuresByDimension: Record<string, QuestionnaireRowDto[]>;
    currentMeasure: QuestionnaireRowDto | undefined;
    organizationId: string | undefined;
    organizationEntityId: string | undefined;
    // Which questionnaire the cached `measuresByDimension` actually belongs to. The dimension
    // cache is reusable across dimension-switches within one questionnaire, but it is **not**
    // valid across questionnaires — switching cells (e.g. year 2024 → 2025) would otherwise
    // hand back the previous questionnaire's rows on a same-dimension activate.
    questionnaireId: string | undefined;
}

export namespace AdvancedMeasureAction {
    // Loads a specific measure from the server and sets it as current measure
    export class Activate {
        public static type = '[AdvancedMeasure UI] Activate Measure';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public questionnaireId: string,
            public dimension: string,
            public lang: string,
            public callCompletion?: Subject<QuestionnaireRowDto[] | undefined>,
        ) {}
    }

    // Creates a new measure and adds it to the list of measures
    export class Create {
        public static type = '[AdvancedMeasure UI] Create Measure';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public measure: QuestionnaireRowDto,
            public dimension: string,
            public callCompletion: Subject<QuestionnaireRowDto>,
        ) {
            // Empty constructor
        }
    }

    // Updates an existing measure on the server and in the local state
    export class Update {
        public static type = '[AdvancedMeasure UI] Update Measure';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public measureId: string,
            public measure: QuestionnaireRowDto,
            public dimension: string,
            public callCompletion: Subject<QuestionnaireRowDto | undefined>,
        ) {}
    }

    // Removes a measure from the server and filters it out of the measures list
    export class Delete {
        public static type = '[AdvancedMeasure UI] Delete Measure';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public measureId: string,
            public callCompletion: Subject<boolean>,
        ) {}
    }

    export class BulkDelete {
        public static type = '[AdvancedMeasure UI] Bulk Delete Measures';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public questionnaireID: string,
            public measureIds: string[],
            public dimension: string,
            public callCompletion: Subject<boolean>,
        ) {}
    }

    export class Move {
        public static type = '[AdvancedMeasure UI] Move Measure';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public destinationQuestionnaireID: string,
            public measureIds: string[],
            public dimension: string,
            public callCompletion: Subject<boolean>,
        ) {}
    }

    export class CopyMulti {
        public static type = '[AdvancedMeasure UI] Copy Measure To Multiple Destinations';
        constructor(
            public organizationId: string,
            public sourceOrganizationEntityId: string,
            public destinationEntityIds: string[],
            public year: number,
            public measureIds: string[],
            public dimension: string,
            public activeQuestionnaireId: string,
            public callCompletion: Subject<CopyToMultipleDestinationsResponseDto>,
        ) {}
    }

    export class BulkUpdate {
        public static type = '[AdvancedMeasure UI] Bulk Update Measures';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public request: BatchUpdateRequestDto,
            public dimension: string,
            public option: keyof BatchUpdateRequestDto,
            public callCompletion: Subject<boolean>,
        ) {}
    }

    export class AddFileToState {
        public static type = '[AdvancedMeasure UI] Add File to Measure';
        constructor(public file: FileInfoDto) {}
    }

    export class RemoveFileToState {
        public static type = '[AdvanceMeasure UI] Remove File to Measure';
        constructor(
            public measureRowId: string,
            public fileId: string,
        ) {}
    }

    // Sets the currently selected measure without server interaction
    export class SetCurrentMeasure {
        public static type = '[AdvancedMeasure UI] Set Current Measure';
        constructor(public measure: QuestionnaireRowDto | undefined) {}
    }

    // Resets all report fields to their default values
    export class ClearState {
        public static type = '[AdvancedMeasure UI] Clear State';
    }
}

@State<AdvancedMeasureModel>({
    name: 'advancedMeasure',
    defaults: {
        measuresByDimension: {},
        currentMeasure: undefined,
        organizationId: undefined,
        organizationEntityId: undefined,
        questionnaireId: undefined,
        isInit: false,
        isLoading: false,
        errors: [],
    },
})
@Injectable()
export class AdvancedMeasureState {
    constructor(
        private advancedMeasureService: AdvancedMeasureHttpService,
        private translateService: CustomTranslateService,
        private store: Store,
    ) {
        this.translateService.onLangChange.subscribe(() => {
            this.store.dispatch(new AdvancedMeasureAction.ClearState());
        });
    }

    //----- store action -----//
    @Action(AdvancedMeasureAction.Activate)
    activate(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.Activate) {
        // If the active questionnaire just changed, the cached per-dimension rows belong to
        // the *previous* questionnaire and must not be served back. Drop them before the
        // cache-hit check below — otherwise activating the same dimension on a different
        // questionnaire (e.g. year 2024 → 2025) would short-circuit and return stale rows.
        const state = ctx.getState();
        if (state.questionnaireId !== action.questionnaireId) {
            ctx.patchState({
                measuresByDimension: {},
                questionnaireId: action.questionnaireId,
            });
        }

        ctx.patchState({
            isLoading: true,
        });

        if (ctx.getState().measuresByDimension[action.dimension]) {
            ctx.patchState({
                isLoading: false,
            });
            return;
        }

        this.advancedMeasureService
            .getAllByDimension(action.organizationId, action.organizationEntityId, {
                questionnaireId: action.questionnaireId,
                dimension: action.dimension,
                locale: action.lang,
            })
            .subscribe({
                next: (measures) => {
                    ctx.patchState({
                        isLoading: false,
                        measuresByDimension: { ...ctx.getState().measuresByDimension, [action.dimension]: measures },
                        organizationId: action.organizationId,
                        organizationEntityId: action.organizationEntityId,
                        errors: [],
                    });
                    if (action.callCompletion) {
                        action.callCompletion.next(measures);
                    }
                },
                error: () => {
                    const errors = ctx.getState().errors;
                    errors.push('Could not load advanced measure');
                    ctx.patchState({
                        currentMeasure: undefined,
                        isLoading: false,
                        errors: errors,
                    });
                    if (action.callCompletion) {
                        action.callCompletion.error(undefined);
                    }
                },
            });
    }

    @Action(AdvancedMeasureAction.Create)
    create(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.Create) {
        ctx.patchState({
            isLoading: true,
        });
        this.advancedMeasureService
            .create(action.organizationId, action.organizationEntityId, action.measure)
            .subscribe({
                next: (createdMeasure) => {
                    // Ensure the measures object is initialized
                    const measures = ctx.getState().measuresByDimension || {};

                    // If the measure has children, we make sure they are related to the parent
                    if (createdMeasure.children?.length) {
                        createdMeasure.children = createdMeasure.children.map((child) => ({
                            ...child,
                            parentId: createdMeasure.id,
                        }));
                    }

                    const updatedMeasuresForDimension = Array.isArray(measures[action.dimension])
                        ? [...measures[action.dimension], createdMeasure]
                        : [createdMeasure];

                    measures[action.dimension] = updatedMeasuresForDimension;

                    ctx.patchState({
                        measuresByDimension: measures,
                        currentMeasure: createdMeasure,
                        isLoading: false,
                        errors: [],
                    });
                    action.callCompletion.next(createdMeasure);
                },
                error: () => {
                    const errors = ctx.getState().errors;
                    errors.push('Could not create advanced measure');
                    ctx.patchState({
                        isLoading: false,
                        errors: errors,
                    });
                    action.callCompletion.error(undefined);
                },
            });
    }

    @Action(AdvancedMeasureAction.Update)
    update(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.Update) {
        ctx.patchState({
            isLoading: true,
        });
        this.advancedMeasureService
            .update(action.organizationId, action.organizationEntityId, action.measure)
            .subscribe({
                next: (updatedMeasure) => {
                    // If the measure has children, we make sure they are related to the parent
                    if (updatedMeasure.children?.length) {
                        updatedMeasure.children = updatedMeasure.children.map((child) => ({
                            ...child,
                            parentId: updatedMeasure.id,
                        }));
                    }

                    const currentMeasures = ctx.getState().measuresByDimension || {};

                    // Update the measure in the appropriate dimension
                    if (currentMeasures[action.dimension]) {
                        const updatedMeasuresForDimension = currentMeasures[action.dimension].map((measure) => {
                            if (measure.id === action.measureId) {
                                return updatedMeasure;
                            }
                            // Also check in children if they exist
                            if (measure.children?.length) {
                                const updatedChildren = measure.children.map((child) =>
                                    child.id === action.measureId ? updatedMeasure : child,
                                );
                                return { ...measure, children: updatedChildren };
                            }
                            return measure;
                        });

                        currentMeasures[action.dimension] = updatedMeasuresForDimension;
                    }

                    ctx.patchState({
                        measuresByDimension: currentMeasures,
                        currentMeasure: updatedMeasure,
                        isLoading: false,
                        errors: [],
                    });
                    action.callCompletion.next(updatedMeasure);
                },
                error: () => {
                    const errors = ctx.getState().errors;
                    errors.push('Could not update advanced measure');
                    ctx.patchState({
                        isLoading: false,
                        errors: errors,
                    });
                    action.callCompletion.error(undefined);
                },
            });
    }

    @Action(AdvancedMeasureAction.AddFileToState)
    AddFileToState(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.AddFileToState) {
        const measures = ctx.getState().measuresByDimension;

        const fileToAdd: FileInfoDto = Array.isArray(action.file) ? (action.file as FileInfoDto[])[0] : action.file;

        let foundMeasure = false;

        const updatedMeasures = Object.keys(measures).reduce(
            (acc, dimension) => {
                acc[dimension] = measures[dimension].map((measure) => {
                    if (measure.id === fileToAdd.questionnaireRowId) {
                        foundMeasure = true;

                        return {
                            ...measure,
                            fileInfos: [...(measure.fileInfos || []), fileToAdd],
                        };
                    }
                    return measure;
                });
                return acc;
            },
            {} as Record<string, QuestionnaireRowDto[]>,
        );

        if (!foundMeasure) {
            console.error("❌ ERREUR: Aucune mesure trouvée avec l'ID:", fileToAdd.questionnaireRowId);
            return;
        }

        ctx.patchState({
            measuresByDimension: updatedMeasures,
        });
    }

    @Action(AdvancedMeasureAction.RemoveFileToState)
    RemoveFileToState(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.RemoveFileToState) {
        const measures = ctx.getState().measuresByDimension;

        const updatedMeasures = Object.keys(measures).reduce(
            (acc, dimension) => {
                acc[dimension] = measures[dimension].map((measure) => {
                    if (measure.id === action.measureRowId) {
                        const updatedFileInfos = measure.fileInfos?.filter((file) => file.id !== action.fileId);

                        return {
                            ...measure,
                            fileInfos: updatedFileInfos,
                        };
                    }
                    return measure;
                });
                return acc;
            },
            {} as Record<string, QuestionnaireRowDto[]>,
        );

        ctx.patchState({
            measuresByDimension: updatedMeasures,
        });
    }

    @Action(AdvancedMeasureAction.Delete)
    delete(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.Delete) {
        ctx.patchState({
            isLoading: true,
        });

        this.advancedMeasureService
            .delete(action.organizationId, action.organizationEntityId, action.measureId)
            .subscribe({
                next: () => {
                    // Clear currentMeasure if it was the deleted one
                    const currentMeasure = ctx.getState().currentMeasure;
                    const newCurrentMeasure = currentMeasure?.id === action.measureId ? undefined : currentMeasure;

                    // Update measures list by removing the deleted item
                    const currentMeasures = { ...ctx.getState().measuresByDimension };
                    if (currentMeasures) {
                        // Remove from all dimensions
                        Object.keys(currentMeasures).forEach((dimension) => {
                            currentMeasures[dimension] = currentMeasures[dimension].filter(
                                (measure) => measure.id !== action.measureId,
                            );
                        });
                    }

                    ctx.patchState({
                        measuresByDimension: currentMeasures,
                        currentMeasure: newCurrentMeasure,
                        isLoading: false,
                        errors: [],
                    });
                    action.callCompletion.next(true);
                },
                error: (error) => {
                    const errors = ctx.getState().errors;
                    errors.push('Could not delete advanced measure');
                    ctx.patchState({
                        isLoading: false,
                        errors: errors,
                    });
                    action.callCompletion.error(error);
                },
            });
    }

    @Action(AdvancedMeasureAction.BulkDelete)
    bulkDelete(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.BulkDelete) {
        ctx.patchState({
            isLoading: true,
        });
        this.advancedMeasureService
            .bulkDelete(action.organizationId, action.organizationEntityId, action.questionnaireID, action.measureIds)
            .subscribe({
                next: () => {
                    //SummaryDoc
                    const questionnaireRowIdsSet = new Set(action.measureIds);
                    const value: number = ctx
                        .getState()
                        .measuresByDimension[action.dimension].filter((measure) =>
                            questionnaireRowIdsSet.has(measure.id ?? ''),
                        )
                        .reduce(
                            (acc, measure) =>
                                acc +
                                (measure.result_co2 ?? 0) +
                                (measure.children?.reduce((acc, child) => acc + (child.result_co2 ?? 0), 0) ?? 0),
                            0,
                        );
                    this.store.dispatch(new QuestionnaireAction.UpdateSummaryDoc(action.dimension, -value));

                    //Update measuresByDimension
                    ctx.patchState({
                        isLoading: false,
                        measuresByDimension: {
                            ...ctx.getState().measuresByDimension,
                            [action.dimension]: ctx
                                .getState()
                                .measuresByDimension[
                                    action.dimension
                                ].filter((measure) => !action.measureIds.includes(measure.id ?? '')),
                        },
                    });
                    action.callCompletion.next(true);
                },
                error: (error) => {
                    const errors = ctx.getState().errors;
                    ctx.patchState({
                        isLoading: false,
                        errors: errors,
                    });
                    action.callCompletion.error(error);
                },
            });
    }

    @Action(AdvancedMeasureAction.Move)
    move(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.Move) {
        ctx.patchState({
            isLoading: true,
        });
        this.advancedMeasureService
            .move(
                action.organizationId,
                action.organizationEntityId,
                action.destinationQuestionnaireID,
                action.measureIds,
            )
            .subscribe({
                next: () => {
                    //SummaryDoc
                    const questionnaireRowIdsSet = new Set(action.measureIds);
                    const value: number = ctx
                        .getState()
                        .measuresByDimension[action.dimension].filter((measure) =>
                            questionnaireRowIdsSet.has(measure.id ?? ''),
                        )
                        .reduce(
                            (acc, measure) =>
                                acc +
                                (measure.result_co2 ?? 0) +
                                (measure.children?.reduce((acc, child) => acc + (child.result_co2 ?? 0), 0) ?? 0),
                            0,
                        );
                    this.store.dispatch(new QuestionnaireAction.UpdateSummaryDoc(action.dimension, -value));

                    //Update measuresByDimension
                    ctx.patchState({
                        isLoading: false,
                        measuresByDimension: {
                            ...ctx.getState().measuresByDimension,
                            [action.dimension]: ctx
                                .getState()
                                .measuresByDimension[
                                    action.dimension
                                ].filter((measure) => !action.measureIds.includes(measure.id ?? '')),
                        },
                    });
                    action.callCompletion.next(true);
                },
                error: (error) => {
                    const errors = ctx.getState().errors;
                    ctx.patchState({
                        isLoading: false,
                        errors: errors,
                    });
                    action.callCompletion.error(error);
                },
            });
    }

    @Action(AdvancedMeasureAction.CopyMulti)
    copyMulti(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.CopyMulti) {
        ctx.patchState({
            isLoading: true,
        });
        this.advancedMeasureService
            .copyMulti(action.organizationId, action.sourceOrganizationEntityId, {
                destinationOrganizationEntityIds: action.destinationEntityIds,
                year: action.year,
                questionnaireRowIds: action.measureIds,
            })
            .subscribe({
                next: (response: CopyToMultipleDestinationsResponseDto) => {
                    // Apply in-state merging only for the destination whose questionnaire is currently active.
                    const activeSuccess = response.succeeded.find(
                        (success) => success.destinationQuestionnaireId === action.activeQuestionnaireId,
                    );
                    if (activeSuccess) {
                        const currentDimensionMeasures = ctx.getState().measuresByDimension[action.dimension];
                        const questionnaireRowIdsSet = new Set(action.measureIds);
                        const mappingsIds = activeSuccess.rowIdMap;
                        const copiedMeasures = currentDimensionMeasures
                            .filter((measure) => questionnaireRowIdsSet.has(measure.id ?? ''))
                            .map((measure) => ({
                                ...measure,
                                comment: measure.comment
                                    ? {
                                          ...measure.comment,
                                          id: mappingsIds[measure.comment.id ?? ''] ?? measure.comment.id,
                                      }
                                    : undefined,
                                id: mappingsIds[measure.id ?? ''] ?? measure.id,
                                children: measure.children?.map((child) => ({
                                    ...child,
                                    id: mappingsIds[child.id ?? ''] ?? child.id,
                                })),
                            }));
                        const updatedDimensionMeasures = [...currentDimensionMeasures, ...copiedMeasures];
                        ctx.patchState({
                            isLoading: false,
                            measuresByDimension: {
                                ...ctx.getState().measuresByDimension,
                                [action.dimension]: updatedDimensionMeasures,
                            },
                        });
                        const value: number = copiedMeasures.reduce(
                            (acc, measure) =>
                                acc +
                                (measure.result_co2 ?? 0) +
                                (measure.children?.reduce((acc, child) => acc + (child.result_co2 ?? 0), 0) ?? 0),
                            0,
                        );
                        this.store.dispatch(new QuestionnaireAction.UpdateSummaryDoc(action.dimension, value));
                    } else {
                        ctx.patchState({
                            isLoading: false,
                        });
                    }
                    action.callCompletion.next(response);
                },
                error: (error) => {
                    const errors = ctx.getState().errors;
                    ctx.patchState({
                        isLoading: false,
                        errors: errors,
                    });
                    action.callCompletion.error(error);
                },
            });
    }

    @Action(AdvancedMeasureAction.BulkUpdate)
    bulkUpdate(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.BulkUpdate) {
        ctx.patchState({
            isLoading: true,
        });
        this.advancedMeasureService
            .bulkUpdate(action.organizationId, action.organizationEntityId, action.request)
            .subscribe({
                next: () => {
                    const questionnaireRowIdsSet = new Set(action.request.questionnaireRowIds);
                    const updatedDimensionMeasures = ctx
                        .getState()
                        .measuresByDimension[action.dimension].map((measure) => {
                            if (questionnaireRowIdsSet.has(measure.id ?? '')) {
                                return {
                                    ...measure,
                                    [action.option]: action.request[action.option],
                                    children: measure.children?.map((child) => ({
                                        ...child,
                                        [action.option]: action.request[action.option],
                                    })),
                                };
                            }
                            return measure;
                        });

                    ctx.patchState({
                        isLoading: false,
                        measuresByDimension: {
                            ...ctx.getState().measuresByDimension,
                            [action.dimension]: updatedDimensionMeasures,
                        },
                    });
                    action.callCompletion.next(true);
                },
                error: (error) => {
                    const errors = ctx.getState().errors;
                    ctx.patchState({
                        isLoading: false,
                        errors: errors,
                    });
                    action.callCompletion.error(error);
                },
            });
    }

    // @Action(AdvancedMeasureAction.SetCurrentMeasure)
    // setCurrentMeasure(ctx: StateContext<AdvancedMeasureModel>, action: AdvancedMeasureAction.SetCurrentMeasure) {
    //     ctx.patchState({
    //         currentMeasure: action.measure,
    //     });
    // }

    @Action(AdvancedMeasureAction.ClearState)
    clearState(ctx: StateContext<AdvancedMeasureModel>, _action: AdvancedMeasureAction.ClearState) {
        ctx.setState({
            measuresByDimension: {},
            currentMeasure: undefined,
            organizationId: undefined,
            organizationEntityId: undefined,
            questionnaireId: undefined,
            isInit: false,
            isLoading: false,
            errors: [],
        });
    }

    //----- selectors -----//
    static getMeasuresByDimension(dimensionId: string) {
        return createSelector([AdvancedMeasureState], (state: AdvancedMeasureModel) => {
            return state?.measuresByDimension?.[dimensionId];
        });
    }

    @Selector([AdvancedMeasureState])
    static getCurrentMeasure(state: AdvancedMeasureModel) {
        return state.currentMeasure;
    }

    @Selector([AdvancedMeasureState])
    static getIsLoading(state: AdvancedMeasureModel) {
        return state.isLoading;
    }

    @Selector([AdvancedMeasureState])
    static getErrors(state: AdvancedMeasureModel) {
        return state.errors;
    }

    @Selector([AdvancedMeasureState])
    static getOrganizationId(state: AdvancedMeasureModel) {
        return state.organizationId;
    }

    @Selector([AdvancedMeasureState])
    static getOrganizationEntityId(state: AdvancedMeasureModel) {
        return state.organizationEntityId;
    }
}
